import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { X, FileText, Phone, PhoneCall, MessageCircle, Mail, Linkedin, Send, Inbox, UserPlus, Rocket, Calendar, CheckCircle } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { toLocaleDateStringInTz } from '../../utils/dateFormatters';
import { useTimezone } from '../../hooks/useTimezone';

interface OverviewModalProps {
  lead: any;
  onClose: () => void;
}

const OverviewModal: React.FC<OverviewModalProps> = ({ lead, onClose }) => {
  const tz = useTimezone();
  const [overviewData, setOverviewData] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchOverviewData = async () => {
      try {
        setIsLoading(true);
        const leadId = lead.lead_id || lead.id;

        const { data, error } = await supabase
          .from('leads_tracking')
          .select('*')
          .eq('lead_id', leadId)
          .maybeSingle();

        if (error) throw error;

        setOverviewData(data);
      } catch (err) {
        console.error('Failed to fetch overview data:', err);
        setError('Failed to load overview data');
      } finally {
        setIsLoading(false);
      }
    };

    if (lead.lead_id || lead.id) {
      fetchOverviewData();
    }
  }, [lead]);

  const formatDateTime = (timestamp: string | null | undefined) => {
    if (!timestamp || timestamp === 'N/A') return null;
    try {
      return toLocaleDateStringInTz(timestamp, tz, 'en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
    } catch {
      return null;
    }
  };

  const getStatusTagStyle = (status: string | null | undefined) => {
    switch (status?.toLowerCase()) {
      case 'new':
        return 'bg-[#1875FF]/10 text-[#1875FF] border-[#1875FF]/30';
      case 'talking':
      case 'chatting':
        return 'bg-[#05E0D1]/10 text-[#0bb5a5] border-[#05E0D1]/30';
      case 'interested':
      case 'engaged':
        return 'bg-[#1875FF]/20 text-[#1875FF] border-[#1875FF]/40';
      case 'meeting booked':
        return 'bg-[#05E0D1]/20 text-[#0bb5a5] border-[#05E0D1]/40';
      case 'closed':
        return 'bg-gray-100 text-gray-500 border-gray-300';
      default:
        return 'bg-gray-50 text-gray-600 border-gray-200';
    }
  };

  const getTempTagStyle = (temp: string | null | undefined) => {
    switch (temp?.toLowerCase()) {
      case 'hot':
        return 'bg-[#FF3B5C]/10 text-[#FF3B5C] border-[#FF3B5C]/30';
      case 'warm':
        return 'bg-[#FF8C00]/10 text-[#FF8C00] border-[#FF8C00]/30';
      case 'cold':
        return 'bg-[#1875FF]/10 text-[#1875FF] border-[#1875FF]/30';
      default:
        return 'bg-gray-50 text-gray-600 border-gray-200';
    }
  };

  const timelineItems = overviewData ? [
    { label: 'Lead Generated', icon: UserPlus, date: formatDateTime(overviewData.created_at) },
    { label: 'Outreach Started', icon: Rocket, date: formatDateTime(overviewData.outreach_start_timestamp) },
    { label: 'Meeting Booked', icon: Calendar, date: formatDateTime(overviewData.meeting_booking_timestamp) },
    { label: 'Meeting Attended', icon: CheckCircle, date: formatDateTime(overviewData.meeting_date) },
  ].filter(item => item.date) : [];

  return ReactDOM.createPortal(
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[85vh] overflow-hidden flex flex-col shadow-2xl shadow-black/20 animate-fadeIn">
        <div className="flex items-center justify-between px-6 py-5 border-b border-[#05E0D1]/20 bg-gradient-to-r from-[#1875FF]/5 to-[#05E0D1]/5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-[#1875FF] to-[#05E0D1] rounded-xl flex items-center justify-center shadow-lg">
              <FileText className="text-white" size={18} />
            </div>
            <div>
              <h3 className="text-lg font-extrabold text-gray-900 font-display">Lead Overview</h3>
              <p className="text-xs text-gray-500 font-medium">{lead.dm_full_name ? `${lead.dm_full_name} - ` : ''}{lead.company_name || 'Unknown Company'}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors p-2 hover:bg-white rounded-lg"
          >
            <X size={20} />
          </button>
        </div>

        <div className="p-6 overflow-y-auto flex-1">
          {isLoading ? (
            <div className="flex items-center justify-center h-64">
              <div className="w-8 h-8 border-2 border-[#1875FF] border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <div className="text-red-500 text-sm font-medium">{error}</div>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-4">
              <OverviewSummaryBox
                status={overviewData?.ovrall_lead_status}
                temp={overviewData?.ovrall_lead_temp}
                summary={overviewData?.ovrall_convo_summary}
                getStatusTagStyle={getStatusTagStyle}
                getTempTagStyle={getTempTagStyle}
              />

              <TimelineBox items={timelineItems} />

              <AgentBox
                title="Call Agent"
                icon={Phone}
                iconColor="text-[#1875FF]"
                leftIcon={Phone}
                rightIcon={PhoneCall}
                leftCount={overviewData?.ca_call_attempts || 0}
                rightCount={overviewData?.ca_call_answered || 0}
                summary={overviewData?.ca_ovrall_calls_convo_summary}
              />

              <AgentBox
                title="WhatsApp Agent"
                icon={MessageCircle}
                iconColor="text-[#05E0D1]"
                leftIcon={Send}
                rightIcon={Inbox}
                leftCount={overviewData?.wa_messages_sent || 0}
                rightCount={overviewData?.wa_replies_received || 0}
                summary={overviewData?.wa_convo_summary}
              />

              <AgentBox
                title="Email Agent"
                icon={Mail}
                iconColor="text-[#1875FF]"
                leftIcon={Send}
                rightIcon={Inbox}
                leftCount={overviewData?.ea_emails_sent || 0}
                rightCount={overviewData?.ea_replies_received || 0}
                summary={overviewData?.ea_convo_summary}
              />

              <AgentBox
                title="LinkedIn Agent"
                icon={Linkedin}
                iconColor="text-[#05E0D1]"
                leftIcon={Send}
                rightIcon={Inbox}
                leftCount={overviewData?.la_messages_sent || 0}
                rightCount={overviewData?.la_replies_received || 0}
                summary={overviewData?.la_convo_summary}
              />
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

const OverviewSummaryBox: React.FC<{
  status: string | null | undefined;
  temp: string | null | undefined;
  summary: string | null | undefined;
  getStatusTagStyle: (s: string | null | undefined) => string;
  getTempTagStyle: (t: string | null | undefined) => string;
}> = ({ status, temp, summary, getStatusTagStyle, getTempTagStyle }) => (
  <div className="bg-gradient-to-br from-white to-gray-50 border-2 border-gray-200 rounded-xl p-5 shadow-sm hover:border-blue-300 hover:shadow-lg hover:shadow-blue-100/50 transition-all">
    <h4 className="text-sm font-bold text-gray-900 font-display mb-3">Overall Summary</h4>
    <div className="flex items-center gap-2 mb-3">
      {status && (
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-semibold border ${getStatusTagStyle(status)}`}>
          {status}
        </span>
      )}
      {temp && (
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-semibold border ${getTempTagStyle(temp)}`}>
          {temp}
        </span>
      )}
    </div>
    <p className="text-xs text-gray-600 leading-relaxed">
      {summary || <span className="text-gray-400 italic">No data yet</span>}
    </p>
  </div>
);

const TimelineBox: React.FC<{
  items: { label: string; icon: React.FC<any>; date: string | null }[];
}> = ({ items }) => (
  <div className="bg-gradient-to-br from-white to-gray-50 border-2 border-gray-200 rounded-xl p-5 shadow-sm hover:border-blue-300 hover:shadow-lg hover:shadow-blue-100/50 transition-all">
    <h4 className="text-sm font-bold text-gray-900 font-display mb-3">Timeline</h4>
    {items.length === 0 ? (
      <p className="text-xs text-gray-400 italic">No data yet</p>
    ) : (
      <div className="grid grid-cols-2 gap-3">
        {items.map((item) => (
          <div key={item.label} className="flex items-start gap-2">
            <div className="w-6 h-6 rounded-lg bg-gradient-to-br from-[#1875FF]/10 to-[#05E0D1]/10 flex items-center justify-center flex-shrink-0 mt-0.5">
              <item.icon size={12} className="text-[#1875FF]" />
            </div>
            <div>
              <span className="text-[11px] font-semibold text-gray-700 block">{item.label}</span>
              <span className="text-[11px] text-gray-500">{item.date}</span>
            </div>
          </div>
        ))}
      </div>
    )}
  </div>
);

const AgentBox: React.FC<{
  title: string;
  icon: React.FC<any>;
  iconColor: string;
  leftIcon: React.FC<any>;
  rightIcon: React.FC<any>;
  leftCount: number;
  rightCount: number;
  summary: string | null | undefined;
}> = ({ title, icon: Icon, iconColor, leftIcon: LeftIcon, rightIcon: RightIcon, leftCount, rightCount, summary }) => (
  <div className="bg-gradient-to-br from-white to-gray-50 border-2 border-gray-200 rounded-xl p-5 shadow-sm hover:border-blue-300 hover:shadow-lg hover:shadow-blue-100/50 transition-all">
    <div className="flex items-center justify-between mb-3">
      <div className="flex items-center gap-2">
        <Icon size={16} className={iconColor} />
        <h4 className="text-sm font-bold text-gray-900 font-display">{title}</h4>
      </div>
      <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-lg bg-gradient-to-r from-[#1875FF]/5 to-[#05E0D1]/5 border border-[#1875FF]/10">
        <div className="flex items-center gap-1">
          <LeftIcon size={10} className="text-[#1875FF]" />
          <span className="text-xs font-bold text-[#1875FF]">{leftCount}</span>
        </div>
        <div className="w-px h-3 bg-gray-300"></div>
        <div className="flex items-center gap-1">
          <RightIcon size={10} className="text-[#05E0D1]" />
          <span className="text-xs font-bold text-[#0bb5a5]">{rightCount}</span>
        </div>
      </div>
    </div>
    <p className="text-xs text-gray-600 leading-relaxed">
      {summary || <span className="text-gray-400 italic">No data yet</span>}
    </p>
  </div>
);

export default OverviewModal;
